import type { EditorCommand, PublicPluginAPI, OptionalPlugin } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import type { ConfluenceUpdateDynamicUiOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { remixRegenerateExperience } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender/remix-regenerate-experience';

import type { OnInlineRemixActionedCallback } from '../command-handlers/command-handler';

// Format mirrors `getMauiUrl` in @atlassian/malleable-ui-components/src/ui/editor-remix/utils/message.ts.
// Inlined here to avoid a cross-package dependency for one URL formula.
const buildMauiUrl = (appId: string): string =>
	`${window.location.origin}/remix/ui/${encodeURIComponent(appId)}`;

export class DocumentDynamicUiUpdateCommandProcessor {
	private onInlineRemixActioned: OnInlineRemixActionedCallback | undefined;

	constructor(
		private editorApi: PublicPluginAPI<
			[AnalyticsPlugin, AIPlugin, OptionalPlugin<MalleableUiPlugin>]
		>,
		onInlineRemixActioned?: OnInlineRemixActionedCallback,
	) {
		this.onInlineRemixActioned = onInlineRemixActioned;
	}

	// Deferred to finish() so the remix-history transaction is dispatched only
	// after endAIExperience(). Dispatching it while the AI session is still open
	// lets a subsequent click/type discard-and-revert the just-applied update.
	private pendingHistoryStackEntryCommand?: EditorCommand;
	private completedExistingInlineUpdateTargetId?: string;
	/** Attempt token captured from the matching selection. */
	private inlineRovoEditAttemptId?: string;

	async streamChunk(
		data: ConfluenceUpdateDynamicUiOnPageData,
		isFinished: boolean,
		_traceId?: string,
		singleInstrumentationId?: string,
		isInlineEdit?: boolean,
	): Promise<void> {
		// Variant-picker updates are inserted FE-side (useUpdateRemix ->
		// useOnComplete); processing the BE push here would double-insert.
		// Rovo-chat updates aren't tracked, so they still flow through.
		if (
			data.previousMauiId &&
			this.editorApi.malleableUi?.actions.isVariantUpdateInFlight(data.previousMauiId) &&
			fg('platform_editor_ai_remix_toolbar')
		) {
			if (isFinished) {
				// The frontend owns variant insertion; the final chunk closes the experience.
				remixRegenerateExperience.success({
					remixType: data.contentType,
					remixSubtype: data.dynamicUiSubtype,
				});
			}
			return;
		}

		this.claimInlineRovoEditAttempt(data.selectionLocalId);
		const attemptId = this.inlineRovoEditAttemptId;
		if ('__isError' in data && (data as { __isError?: boolean }).__isError) {
			this.reportInlineRovoEditAttempt('generation-error', attemptId);
			return;
		}
		if (!isFinished) {
			return;
		}

		// A safety / AUP violation is an expected handoff. It has no editor replacement to await.
		if (data.isSafetyViolation) {
			this.reportInlineRovoEditAttempt('handoff', attemptId);
			remixRegenerateExperience.success({
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});
			return;
		}

		// Awaiting matters: it keeps the existing AI experience overlay covering
		// the duplicate roundtrip. `endAIExperience()` is only called from
		// `finish()` after this resolves, via PageUpdateCommandHandler.finishAction.
		try {
			const duplicateAndReplaceMauiApp =
				this.editorApi.malleableUi?.actions.duplicateAndReplaceMauiApp;
			if (!duplicateAndReplaceMauiApp) {
				if (attemptId) {
					this.reportInlineRovoEditAttempt('insertion-failed', attemptId);
					return;
				}
			}
			if (duplicateAndReplaceMauiApp) {
				this.reportInlineRovoEditAttempt('result-ready', attemptId);
				this.reportInlineRovoEditAttempt('direct-insertion-started', attemptId);
			}
			// `duplicateAndReplaceMauiApp` may duplicate the BE-provided MAUI app
			// under the current page (for permissions). When it does, the live
			// node ends up with the duplicated id — NOT `data.id`. Capture that
			// id so the stack entry URL we record matches the live node, which
			// is what the toolbar lookup keys off.
			const replaceResult = duplicateAndReplaceMauiApp
				? await duplicateAndReplaceMauiApp({
						previousMauiId: data.previousMauiId,
						newMauiId: data.id,
						localId: data.selectionLocalId,
						requireInsertionAcknowledgement: Boolean(attemptId),
					})
				: undefined;
			if (!replaceResult && attemptId) {
				this.reportInlineRovoEditAttempt('insertion-failed', attemptId);
				return;
			}
			if (replaceResult && isInlineEdit && data.previousMauiId && data.selectionLocalId) {
				this.onInlineRemixActioned?.({
					actionKey: 'dynamic-ui-create',
					createdContentMetadata: {
						previousMauiId: data.previousMauiId,
						remixId: replaceResult.insertedMauiId,
					},
					createdContentType: data.contentType ?? 'dynamic-ui',
					dynamicUiSubtype: data.dynamicUiSubtype,
					identity: {
						attribute: 'mauiVersion',
						localId: data.selectionLocalId,
						value: replaceResult.insertedMauiId,
					},
					singleInstrumentationId,
				});
			}
			if (replaceResult && data.previousMauiId && data.selectionLocalId) {
				this.completedExistingInlineUpdateTargetId = data.selectionLocalId;
			}
			remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.NODE_INSERTED);
			remixEditExistingExperience.success();
			this.reportInlineRovoEditAttempt('inserted', attemptId);
			remixRegenerateExperience.success({
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});

			if (data.previousMauiId && fg('platform_editor_ai_remix_toolbar')) {
				// Fall back to data.id if the action didn't run (e.g. malleableUi
				// plugin unavailable) — better to record an entry keyed off the
				// BE id than to skip recording entirely.
				const insertedMauiId = replaceResult?.insertedMauiId ?? data.id;
				// Forward the backend-confirmed subtype so a type-changing edit
				// ("change to a bar chart", "make it a tree map") updates the style
				// picker. The reducer falls back to inheriting the previous subtype
				// when the backend omits one.
				const backendSubtype = data.dynamicUiSubtype;
				// Stash the command; it is dispatched in finish() after the AI
				// experience ends (see field comment).
				this.pendingHistoryStackEntryCommand =
					this.editorApi.malleableUi?.commands.addBackendRemixHistoryStackEntry({
						previousMauiId: data.previousMauiId,
						url: buildMauiUrl(insertedMauiId),
						dynamicUiType: data.contentType,
						dynamicUiSubtype: backendSubtype,
					});
			}
		} catch (error) {
			this.reportInlineRovoEditAttempt('insertion-failed', attemptId);
			remixEditExistingExperience.failure({
				metadata: { reason: 'duplicate_replace_failed' },
			});
			remixRegenerateExperience.fail({
				error: error instanceof Error ? error.message : 'duplicate_replace_failed',
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});
			throw error;
		}
	}

	private claimInlineRovoEditAttempt(targetId: string | undefined): void {
		if (this.inlineRovoEditAttemptId || !targetId) {
			return;
		}
		this.inlineRovoEditAttemptId =
			this.editorApi.malleableUi?.actions.claimInlineRovoEdit?.(targetId);
	}

	private reportInlineRovoEditAttempt(
		result:
			| 'generation-aborted'
			| 'handoff'
			| 'insertion-failed'
			| 'inserted'
			| 'generation-error'
			| 'result-ready'
			| 'direct-insertion-started',
		attemptId = this.inlineRovoEditAttemptId,
	): void {
		if (!attemptId) {
			return;
		}
		this.editorApi.malleableUi?.actions.reportInlineRovoEdit?.(attemptId, result);
		if (
			result === 'generation-aborted' ||
			result === 'handoff' ||
			result === 'insertion-failed' ||
			result === 'inserted' ||
			result === 'generation-error'
		) {
			if (this.inlineRovoEditAttemptId === attemptId) {
				this.inlineRovoEditAttemptId = undefined;
			}
		}
	}

	abort(): void {
		this.reportInlineRovoEditAttempt('generation-aborted');
	}

	streamError(): void {
		this.reportInlineRovoEditAttempt('generation-error');
	}

	finish(): void {
		const aiDecorationState =
			this.editorApi.aiExperience?.sharedState?.currentState()?.aiDecorationExperiencePluginState;
		const completedInlineUpdateTargetId = this.completedExistingInlineUpdateTargetId;
		const isReadyBarCandidate =
			Boolean(completedInlineUpdateTargetId) &&
			aiDecorationState?.inlineRovoStreamingTargetId === completedInlineUpdateTargetId &&
			!aiDecorationState?.isInlineEditReady &&
			isExperimentEnabled('cc_maui_polish_changes_batch_6');
		const isInlineEditTargetInView = isReadyBarCandidate
			? this.editorApi.aiExperience?.actions.isInlineEditTargetInView?.(
					completedInlineUpdateTargetId,
				)
			: undefined;
		if (isReadyBarCandidate && isInlineEditTargetInView !== true) {
			this.editorApi.aiExperience?.actions.endInlineRovoStreaming?.();
			this.editorApi.aiExperience?.actions.showInlineEditReady?.(completedInlineUpdateTargetId);
		}
		this.completedExistingInlineUpdateTargetId = undefined;
		this.editorApi.aiExperience?.actions.endAIExperience();
		// Record the remix-history stack entry only after the AI experience has
		// ended, so the transaction does not land in the still-open session where a
		// click/type would discard-and-revert the update.
		if (this.pendingHistoryStackEntryCommand) {
			this.editorApi.core?.actions.execute(this.pendingHistoryStackEntryCommand);
			this.pendingHistoryStackEntryCommand = undefined;
		}
	}
}
